/**
 * BotonItinerarioMusical.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Botón siempre visible. Comportamiento:
 *   • Evento CON audio  → genera y descarga el PDF de requerimientos.
 *   • Evento SIN audio  → muestra un Dialog explicativo (no descarga nada).
 *
 * Detección de audio: busca palabras clave en paquete/servicio/titulo/descripcion:
 *   audio, sonido, música, musica, dj, cabina, karaoke
 * ─────────────────────────────────────────────────────────────────────────────
 */
import React, { useState } from "react";
import { pdf } from "@react-pdf/renderer";
import {
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import { MusicNote as MusicNoteIcon } from "@mui/icons-material";
import ItinerarioMusicalPDF from "./ItinerarioMusicalPDF";
import logoSrc from "../images/R1.webp";

// ── Detecta si el evento tiene servicio/paquete de audio ─────────────────────
// Nota: se evita la palabra genérica "cabina" a solas porque también se usa
// para cabinas de fotos (Cabina 360°, Cabina Inflable), que NO son audio.
const PALABRAS_AUDIO = [
  "audio",
  "sonido",
  "música",
  "musica",
  "dj",
  "karaoke",
  "cabina de audio",
  "cabina de sonido",
  "cabina dj",
];

export const tieneAudio = (evento) => {
  if (!evento) return false;
  const campos = [
    evento.paquete_nombre,
    evento.tipo_servicio_nombre,
    evento.titulo,
    evento.descripcion,
  ];
  const texto = campos
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, ""); // quitar acentos para comparar

  return PALABRAS_AUDIO.some((kw) =>
    texto.includes(kw.normalize("NFD").replace(/[\u0300-\u036f]/g, ""))
  );
};

// ── Convierte webp/cualquier imagen a data URL PNG via canvas ─────────────────
const toDataUrlPng = (src) =>
  new Promise((resolve) => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width  = img.naturalWidth  || img.width;
        canvas.height = img.naturalHeight || img.height;
        canvas.getContext("2d").drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

// ── Componente ────────────────────────────────────────────────────────────────
const BotonItinerarioMusical = ({ evento, size = "small", sx = {} }) => {
  const [loading, setLoading]       = useState(false);
  const [snack, setSnack]           = useState({ open: false, msg: "", sev: "success" });

  const conAudio = tieneAudio(evento);

  const handleClick = async () => {
    if (!evento || !conAudio) return;

    // Con audio → generar y descargar PDF
    setLoading(true);
    console.log("[BotonItinerarioMusical] 🔄 Iniciando generación de PDF para:", evento.titulo);
    
    try {
      const logoUrl = await toDataUrlPng(logoSrc);
      console.log("[BotonItinerarioMusical] ✅ Logo convertido a PNG");
      
      const blob = await pdf(
        <ItinerarioMusicalPDF evento={evento} logoUrl={logoUrl} />
      ).toBlob();

      console.log("[BotonItinerarioMusical] ✅ Blob PDF generado, tamaño:", blob.size, "bytes");

      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `Itinerario-Musical-${(evento.cliente_nombre || "Cliente").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(url), 5000);

      console.log("[BotonItinerarioMusical] ✅✅ PDF descargado exitosamente");
      setSnack({ open: true, msg: "✅ Itinerario musical descargado correctamente", sev: "success" });
    } catch (err) {
      console.error("[BotonItinerarioMusical] ❌ Error al generar PDF:", err);
      console.error("[BotonItinerarioMusical] Error type:", err.name);
      console.error("[BotonItinerarioMusical] Error message:", err.message);
      console.error("[BotonItinerarioMusical] Stack trace:", err.stack);
      
      // Diferenciar tipos de errores para mensajes más específicos
      let mensajeError = "Error desconocido al generar el PDF";
      
      if (err.message.includes("Unknown font")) {
        mensajeError = "❌ Error de fuente: El archivo de fuente no se cargó correctamente. Intenta de nuevo.";
        console.error("[BotonItinerarioMusical] 🔧 Recomendación: Verifica la conexión a Google Fonts");
      } else if (err.message.includes("fetch")) {
        mensajeError = "❌ Error de conexión: No se pudo cargar los recursos. Verifica tu conexión a Internet.";
      } else if (err.message.includes("timeout")) {
        mensajeError = "❌ Timeout: La operación tardó demasiado. Intenta de nuevo.";
      } else {
        mensajeError = `❌ ${err.message}`;
      }
      
      setSnack({ open: true, msg: mensajeError, sev: "error" });
    } finally {
      setLoading(false);
    }
  };

  const paqueteServicio = evento?.paquete_nombre || evento?.tipo_servicio_nombre || "—";

  return (
    <>
      {/* ── Botón: deshabilitado si el evento no tiene servicio/paquete de audio ── */}
      <Tooltip
        title={
          conAudio
            ? ""
            : `No disponible: el evento tiene contratado "${paqueteServicio}", sin servicio o paquete de música/audio.`
        }
        disableHoverListener={conAudio}
      >
        <span>
          <Button
            variant="outlined"
            size={size}
            disabled={loading || !conAudio}
            onClick={handleClick}
            startIcon={
              loading ? <CircularProgress size={14} /> : <MusicNoteIcon />
            }
            sx={{
              borderColor: "#7C3AED",
              color:       "#7C3AED",
              "&:hover": { borderColor: "#5B21B6", bgcolor: "rgba(124,58,237,0.06)" },
              ...sx,
            }}
          >
            Itinerario Musical
          </Button>
        </span>
      </Tooltip>

      {/* ── Snackbar de éxito / error ── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.sev}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BotonItinerarioMusical;
